import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import { Plus, X } from "@phosphor-icons/react";
import { formatDateTime } from "@/lib/date";

export default function Friends() {
  const { t, lang } = useLang();
  const [refs, setRefs] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", country: "United Kingdom", preferred_contact: "whatsapp", consent: false });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/referrals").then((r) => setRefs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Consent required");
    setLoading(true);
    try {
      await api.post("/referrals", form);
      toast.success(t.friends.form.success);
      setForm({ first_name: "", last_name: "", phone: "", email: "", country: "United Kingdom", preferred_contact: "whatsapp", consent: false });
      setOpenForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif-display text-4xl">{t.friends.title}</h1>
          <p className="text-[#3b4b5a] mt-2">{t.friends.sub}</p>
        </div>
        <button data-testid="open-add-friend-btn" onClick={() => setOpenForm(true)} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0d3b66] text-[#f4a261] text-sm hover:bg-[#071f36]">
          <Plus size={14} weight="bold" /> {t.friends.addManual}
        </button>
      </div>

      <div className="rounded-3xl bg-white border border-[#e6dfd2] overflow-hidden">
        {refs.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#6b7280]">{t.friends.empty}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-[#6b7280] border-b border-[#e6dfd2]">
                <th className="text-left p-4">{t.friends.headers.name}</th>
                <th className="text-left p-4">{t.friends.headers.added}</th>
                <th className="text-left p-4">{t.friends.headers.status}</th>
                <th className="text-left p-4">{t.friends.headers.source}</th>
                <th className="text-left p-4">{t.friends.headers.reward}</th>
              </tr>
            </thead>
            <tbody>
              {refs.map((r) => (
                <tr key={r.id} className="border-b border-[#e6dfd2] last:border-0 row-hover">
                  <td className="p-4 font-medium">{r.display_name}</td>
                  <td className="p-4 text-[#6b7280] text-xs">{formatDateTime(r.created_at)}</td>
                  <td className="p-4"><span className="inline-block px-3 py-1 rounded-full bg-[#f6f2ea] border border-[#e6dfd2] text-xs">{t.statuses[r.status]}</span></td>
                  <td className="p-4 text-[#6b7280] capitalize">{r.source}</td>
                  <td className="p-4">
                    {r.reward_amount
                      ? (r.reward_type === "amazon" ? `£${r.reward_amount}` : (lang === "tr" ? r.reward_label_tr : r.reward_label_en))
                      : <span className="text-[#6b7280]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpenForm(false)}>
          <div className="w-full max-w-lg rounded-3xl bg-white p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button data-testid="close-form-btn" onClick={() => setOpenForm(false)} className="absolute top-4 right-4 w-9 h-9 rounded-full border border-[#e6dfd2] flex items-center justify-center hover:border-[#0d3b66]"><X size={14} /></button>
            <h2 className="font-serif-display text-3xl">{t.friends.form.title}</h2>
            <p className="text-sm text-[#3b4b5a] mt-2">{t.friends.form.sub}</p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="friend-first-name" required placeholder={t.friends.form.firstName} value={form.first_name} onChange={set("first_name")} className="input" />
                <input data-testid="friend-last-name" required placeholder={t.friends.form.lastName} value={form.last_name} onChange={set("last_name")} className="input" />
              </div>
              <input data-testid="friend-phone" required placeholder={t.friends.form.phone} value={form.phone} onChange={set("phone")} className="input" />
              <input data-testid="friend-email" type="email" placeholder={t.friends.form.email} value={form.email} onChange={set("email")} className="input" />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder={t.friends.form.country} value={form.country} onChange={set("country")} className="input" />
                <select data-testid="friend-preferred" value={form.preferred_contact} onChange={set("preferred_contact")} className="input">
                  <option value="whatsapp">WhatsApp</option>
                  <option value="phone">{t.friends.form.phone}</option>
                  <option value="email">{t.friends.form.email}</option>
                </select>
              </div>
              <label className="flex items-start gap-3 text-xs text-[#3b4b5a] cursor-pointer">
                <input data-testid="friend-consent" type="checkbox" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-0.5" />
                <span>{t.friends.form.consent}</span>
              </label>
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setOpenForm(false)} className="px-5 py-2.5 rounded-full border border-[#e6dfd2] text-sm hover:border-[#0d3b66]">{t.friends.form.cancel}</button>
                <button data-testid="friend-submit-btn" disabled={loading} type="submit" className="px-5 py-2.5 rounded-full bg-[#0d3b66] text-[#f4a261] text-sm disabled:opacity-60 hover:bg-[#071f36]">
                  {loading ? t.common.loading : t.friends.form.submit}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .input { width:100%; padding:11px 14px; border-radius:10px; background:white; border:1px solid #e6dfd2; font-size:14px; outline:none; }
        .input:focus { border-color:#0d3b66; box-shadow:0 0 0 3px rgba(13,59,102,0.08); }
      `}</style>
    </div>
  );
}
